import React, { useState, useEffect } from "react";
import InputText from "../../Inputs/Text";
import InputSelect from "../../Inputs/Select";
import RadioButton from "../../Inputs/Radio";
import { isOnlyNumbers } from "../../../utilities/Validations";
import {
  getMarcas,
  getModelos,
  getAniosModelo,
  getVersiones,
} from "../../../API/TradeInRequest";

const transmisionOptions = [
  { value: "Manual", label: "Manual" },
  { value: "Automática", label: "Automática" },
];

const propietariosOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5+", label: "5 o más" },
];

// Mapea la respuesta a opciones para InputSelect.
// Como no conocemos la estructura aún, intenta varios nombres comunes y
// loguea cada item para poder ajustar después.
const mapToOptions = (data, catalog) => {
  console.log(`[TradeIn][${catalog}] Raw data:`, data);

  if (!Array.isArray(data)) {
    console.warn(`[TradeIn][${catalog}] Se esperaba un array, se recibió:`, typeof data, data);
    return [];
  }

  return data.map((item, index) => {
    const value =
      item.id ??
      item.idMarca ??
      item.idModelo ??
      item.idAnio ??
      item.idAnioModelo ??
      item.idVersion ??
      item._id ??
      String(index);

    const label =
      item.nombre ??
      item.name ??
      item.descripcion ??
      item.marca ??
      item.modelo ??
      item.anio ??
      item.anioModelo ??
      item.version ??
      item.label ??
      JSON.stringify(item);

    console.log(`[TradeIn][${catalog}] item[${index}]`, { value, label, raw: item });
    return { value: String(value), label: String(label) };
  });
};

const TradeInSection = ({ onTradeInChange }) => {

  // DEBUG: llamadas hardcodeadas al montar para inspeccionar estructura de respuesta
  useEffect(() => {
    const token = process.env.REACT_APP_TOMA_DE_AUTO_SERVICIOS_DIGITALES_TOKEN_CATALOGOS;
    const headers = { Authorization: `Bearer ${token}` };

    console.log("[TradeIn][DEBUG] Token catalogos:", token);

    Promise.allSettled([
      fetch("https://tda-qa.vwmapps.com.mx/api-qa/marcas", { headers }),
      fetch("https://tda-qa.vwmapps.com.mx/api-qa/modelos?idMarca=v_1_57", { headers }),
      fetch("https://tda-qa.vwmapps.com.mx/api-qa/aniosmodelo?idModelo=v_1_57_23", { headers }),
      fetch("https://tda-qa.vwmapps.com.mx/api-qa/versiones?idAnio=v_1_57_23_40", { headers }),
    ]).then(async (results) => {
      const labels = ["marcas", "modelos", "aniosmodelo", "versiones"];
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status === "fulfilled") {
          const res = result.value;
          console.log(`[TradeIn][DEBUG][${labels[i]}] status:`, res.status, res.statusText);
          try {
            const json = await res.json();
            console.log(`[TradeIn][DEBUG][${labels[i]}] body:`, json);
          } catch (e) {
            console.warn(`[TradeIn][DEBUG][${labels[i]}] no se pudo parsear JSON:`, e);
          }
        } else {
          console.error(`[TradeIn][DEBUG][${labels[i]}] fetch rechazado:`, result.reason);
        }
      }
    });
  }, []);

  const [hasTradeIn, setHasTradeIn] = useState(null);
  const [carData, setCarData] = useState({
    marca: "",
    modelo: "",
    anioModelo: "",
    version: "",
    transmision: "",
    numeroPropietarios: "",
    color: "",
    kilometraje: "",
    pagosCorriente: null,
    todosDocumentos: null,
  });

  // IDs seleccionados para las llamadas en cascada
  const [selectedMarcaId, setSelectedMarcaId] = useState("");
  const [selectedModeloId, setSelectedModeloId] = useState("");
  const [selectedAnioId, setSelectedAnioId] = useState("");

  // Opciones de los catálogos
  const [marcasOptions, setMarcasOptions] = useState([]);
  const [modelosOptions, setModelosOptions] = useState([]);
  const [aniosOptions, setAniosOptions] = useState([]);
  const [versionesOptions, setVersionesOptions] = useState([]);

  // Estados de carga
  const [loadingMarcas, setLoadingMarcas] = useState(true);
  const [loadingModelos, setLoadingModelos] = useState(false);
  const [loadingAnios, setLoadingAnios] = useState(false);
  const [loadingVersiones, setLoadingVersiones] = useState(false);

  // Cargar marcas al montar
  useEffect(() => {
    console.log("[TradeIn] Fetching marcas...");
    getMarcas()
      .then((res) => {
        console.log("[TradeIn] getMarcas full response:", res);
        const data = res.data?.data ?? res.data ?? [];
        setMarcasOptions(mapToOptions(data, "marcas"));
      })
      .catch((err) => {
        console.error("[TradeIn] Error al obtener marcas:", err?.response ?? err);
      })
      .finally(() => setLoadingMarcas(false));
  }, []);

  // Cargar modelos cuando cambia la marca
  useEffect(() => {
    if (!selectedMarcaId) return;
    setModelosOptions([]);
    setAniosOptions([]);
    setVersionesOptions([]);
    setLoadingModelos(true);
    console.log("[TradeIn] Fetching modelos para idMarca:", selectedMarcaId);
    getModelos(selectedMarcaId)
      .then((res) => {
        console.log("[TradeIn] getModelos full response:", res);
        const data = res.data?.data ?? res.data ?? [];
        setModelosOptions(mapToOptions(data, "modelos"));
      })
      .catch((err) => {
        console.error("[TradeIn] Error al obtener modelos:", err?.response ?? err);
      })
      .finally(() => setLoadingModelos(false));
  }, [selectedMarcaId]);

  // Cargar años cuando cambia el modelo
  useEffect(() => {
    if (!selectedModeloId) return;
    setAniosOptions([]);
    setVersionesOptions([]);
    setLoadingAnios(true);
    console.log("[TradeIn] Fetching años para idModelo:", selectedModeloId);
    getAniosModelo(selectedModeloId)
      .then((res) => {
        console.log("[TradeIn] getAniosModelo full response:", res);
        const data = res.data?.data ?? res.data ?? [];
        setAniosOptions(mapToOptions(data, "años"));
      })
      .catch((err) => {
        console.error("[TradeIn] Error al obtener años:", err?.response ?? err);
      })
      .finally(() => setLoadingAnios(false));
  }, [selectedModeloId]);

  // Cargar versiones cuando cambia el año
  useEffect(() => {
    if (!selectedAnioId) return;
    setVersionesOptions([]);
    setLoadingVersiones(true);
    console.log("[TradeIn] Fetching versiones para idAnio:", selectedAnioId);
    getVersiones(selectedAnioId)
      .then((res) => {
        console.log("[TradeIn] getVersiones full response:", res);
        const data = res.data?.data ?? res.data ?? [];
        setVersionesOptions(mapToOptions(data, "versiones"));
      })
      .catch((err) => {
        console.error("[TradeIn] Error al obtener versiones:", err?.response ?? err);
      })
      .finally(() => setLoadingVersiones(false));
  }, [selectedAnioId]);

  const updateField = (field, value) => {
    setCarData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (hasTradeIn === "si") {
      const data = {
        marca: carData.marca,
        modelo: carData.modelo,
        anioModelo: carData.anioModelo,
        version: carData.version,
        transmision: carData.transmision,
        numeroPropietarios: carData.numeroPropietarios !== "" ? parseInt(carData.numeroPropietarios) : 0,
        color: carData.color,
        kilometraje: carData.kilometraje !== "" ? parseInt(carData.kilometraje) : 0,
        pagosCorriente: carData.pagosCorriente === true,
        todosDocumentos: carData.todosDocumentos === true,
        pagando: carData.pagosCorriente === true,
      };
      onTradeInChange(data);
    } else {
      onTradeInChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTradeIn, carData]);

  const handleTradeInChange = (value) => {
    setHasTradeIn(value);
    if (value === "no") {
      setCarData({
        marca: "",
        modelo: "",
        anioModelo: "",
        version: "",
        transmision: "",
        numeroPropietarios: "",
        color: "",
        kilometraje: "",
        pagosCorriente: null,
        todosDocumentos: null,
      });
      setSelectedMarcaId("");
      setSelectedModeloId("");
      setSelectedAnioId("");
      setModelosOptions([]);
      setAniosOptions([]);
      setVersionesOptions([]);
    }
  };

  const handleMarcaChange = (e) => {
    const id = e.target.value;
    const option = marcasOptions.find((o) => o.value === id);
    console.log("[TradeIn] Marca seleccionada:", { id, option });
    setSelectedMarcaId(id);
    setSelectedModeloId("");
    setSelectedAnioId("");
    updateField("marca", option?.label ?? id);
    updateField("modelo", "");
    updateField("anioModelo", "");
    updateField("version", "");
  };

  const handleModeloChange = (e) => {
    const id = e.target.value;
    const option = modelosOptions.find((o) => o.value === id);
    console.log("[TradeIn] Modelo seleccionado:", { id, option });
    setSelectedModeloId(id);
    setSelectedAnioId("");
    updateField("modelo", option?.label ?? id);
    updateField("anioModelo", "");
    updateField("version", "");
  };

  const handleAnioChange = (e) => {
    const id = e.target.value;
    const option = aniosOptions.find((o) => o.value === id);
    console.log("[TradeIn] Año seleccionado:", { id, option });
    setSelectedAnioId(id);
    updateField("anioModelo", option?.label ?? id);
    updateField("version", "");
  };

  const handleVersionChange = (e) => {
    const id = e.target.value;
    const option = versionesOptions.find((o) => o.value === id);
    console.log("[TradeIn] Versión seleccionada:", { id, option });
    updateField("version", option?.label ?? id);
  };

  return (
    <div className="trade-in-section mt-5 pt-5">
      <div className="trade-in-question mt-4">
        <h2>¿Cuentas con un vehículo para toma de auto? <span className="tooltip-trade-in">i

          <div className="tooltip-trade-in-content">
            <p>Toma de Auto se refiere a que tienes un vehículo de
              tu propiedad, de cualquier marca, ya sea pagado o en
              proceso de pagos, que deseas entregar a cuenta para
              la compra de un vehículo Audi.</p>
          </div>
        </span></h2>


        <div className="trade-in-radio-group ms-5">
          <RadioButton
            name="hasTradeIn"
            value="si"
            checked={hasTradeIn === "si"}
            onChange={() => handleTradeInChange("si")}
            label="Sí"
          />
          <RadioButton
            name="hasTradeIn"
            value="no"
            checked={hasTradeIn === "no"}
            onChange={() => handleTradeInChange("no")}
            label="No"
          />
        </div>
      </div>

      <div className={`trade-in-fields ${hasTradeIn === "si" ? "trade-in-fields--open" : ""}`}>
        <div className="fields-grid mt-4 mb-5">
          <div>
            <InputSelect
              label="¿Qué marca es?"
              name="tradeInMarca"
              options={marcasOptions}
              defaultValue={selectedMarcaId}
              onChange={handleMarcaChange}
              disabled={loadingMarcas}
              noDataMessage="No hay marcas disponibles"
              showNoDataMessage={!loadingMarcas && marcasOptions.length === 0}
            />
          </div>
          <div>
            <InputSelect
              label="¿Qué modelo es?"
              name="tradeInModelo"
              options={modelosOptions}
              defaultValue={selectedModeloId}
              onChange={handleModeloChange}
              disabled={!selectedMarcaId || loadingModelos}
              noDataMessage="No hay modelos disponibles"
              showNoDataMessage={selectedMarcaId && !loadingModelos && modelosOptions.length === 0}
            />
          </div>
          <div>
            <InputSelect
              label="¿Qué año modelo es?"
              name="tradeInAnio"
              options={aniosOptions}
              defaultValue={selectedAnioId}
              onChange={handleAnioChange}
              disabled={!selectedModeloId || loadingAnios}
              noDataMessage="No hay años disponibles"
              showNoDataMessage={selectedModeloId && !loadingAnios && aniosOptions.length === 0}
            />
          </div>
          <div>
            <InputSelect
              label="¿Qué versión es?"
              name="tradeInVersion"
              options={versionesOptions}
              defaultValue={carData.version}
              onChange={handleVersionChange}
              disabled={!selectedAnioId || loadingVersiones}
              noDataMessage="No hay versiones disponibles"
              showNoDataMessage={selectedAnioId && !loadingVersiones && versionesOptions.length === 0}
            />
          </div>
          <div>
            <InputSelect
              label="¿Qué transmisión tiene?"
              name="tradeInTransmision"
              options={transmisionOptions}
              onChange={(e) => updateField("transmision", e.target.value)}
              defaultValue={carData.transmision}
            />
          </div>
          <div>
            <InputSelect
              label="¿Cuántos propietarios ha tenido?"
              name="tradeInPropietarios"
              options={propietariosOptions}
              onChange={(e) => updateField("numeroPropietarios", e.target.value)}
              defaultValue={carData.numeroPropietarios}
            />
          </div>
          <div>
            <InputText
              label="¿Qué color es?"
              name="tradeInColor"
              secondaryLabel="Color"
              maxLength={30}
              onBlur={(e) => updateField("color", e.target.value.trim())}
            />
          </div>
          <div>
            <InputText
              label="¿Qué kilometraje tiene?"
              name="tradeInKilometraje"
              secondaryLabel="Kilometraje"
              maxLength={7}
              value={carData.kilometraje}
              onChange={(e) => {
                if (e.target.value.length <= 7) {
                  updateField("kilometraje", isOnlyNumbers(e.target.value));
                }
              }}
            />
          </div>
        </div>

        <div className="trade-in-extra-questions mt-4">
          <div className="trade-in-extra-question mb-4">
            <p className="mb-3">¿El auto cuenta con pagos al corriente?</p>
            <div className="trade-in-radio-group">
              <RadioButton
                name="pagosCorriente"
                value="si"
                checked={carData.pagosCorriente === true}
                onChange={() => updateField("pagosCorriente", true)}
                label="Sí"
              />
              <RadioButton
                name="pagosCorriente"
                value="no"
                checked={carData.pagosCorriente === false}
                onChange={() => updateField("pagosCorriente", false)}
                label="No"
              />
            </div>
          </div>
          <div className="trade-in-extra-question">
            <p className="mb-3">¿El auto cuenta con toda la documentación?</p>
            <div className="trade-in-radio-group">
              <RadioButton
                name="todosDocumentos"
                value="si"
                checked={carData.todosDocumentos === true}
                onChange={() => updateField("todosDocumentos", true)}
                label="Sí"
              />
              <RadioButton
                name="todosDocumentos"
                value="no"
                checked={carData.todosDocumentos === false}
                onChange={() => updateField("todosDocumentos", false)}
                label="No"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeInSection;
